// Authentication state hook. Backed by /api/auth/me which always returns 200
// with { authenticated: boolean } WHEN the request reaches the server. We map
// that to a state machine so the router can show a loading screen while we
// don't yet know if the cookie is valid.
//
// Sprint access-session-recovery (D2) — `gate-expired` joins `unauthed` as a
// THIRD, distinct failure of `/api/auth/me`: the Cloudflare Access identity
// gate rejected the request before it ever reached the server (D1), as
// opposed to the server answering "no application session". Conflating the
// two (the pre-sprint behaviour) is exactly what turned D013-EC-560 into "the
// password form is up and every submit fails" for an already-installed
// client with no path back in.

import { useCallback, useEffect, useState } from 'react';
import { api, ApiHttpError, isAccessGateError } from '../lib/api';

export type AuthState = 'unknown' | 'authed' | 'unauthed' | 'gate-expired';

interface MeResponse {
  authenticated: boolean;
}

export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>('unknown');
  // Challenger R1 change 12 (engine defence) — set ONLY by the initial boot
  // check below, never by `login()`'s own failures. If the browser never
  // produces an `opaqueredirect` for an Access rejection (observed on
  // WebKit), the gate is indistinguishable from "no network" at the
  // classifier level and `authState` stays `unauthed` — this flag is the
  // one extra bit LoginPage needs to still offer the D3 way back on that
  // engine, without adding a heuristic to the classifier itself.
  const [meNetworkError, setMeNetworkError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api<MeResponse>('/api/auth/me')
      .then((r) => {
        if (cancelled) return;
        setAuthState(r.authenticated ? 'authed' : 'unauthed');
      })
      .catch((err) => {
        if (cancelled) return;
        if (isAccessGateError(err)) {
          setAuthState('gate-expired');
          return;
        }
        setMeNetworkError(err instanceof ApiHttpError && err.code === 'NETWORK_ERROR');
        setAuthState('unauthed');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (password: string) => {
    // Fence crossing (universal-principles.md "Chesterton's Fence",
    // documented per sprint-workflow.md §Root cause / results doc §Fences):
    // pre-sprint this was
    //   try { await api(...) } catch (err) { if (err instanceof ApiHttpError) throw err; throw new ApiHttpError(0, 'NETWORK_ERROR', ...) }
    // — a defensive re-wrap for the case `api()` threw something OTHER than
    // an `ApiHttpError` (a raw `TypeError` from an unclassified `fetch`
    // rejection). D1 makes `api()` itself normalise every failure path
    // (Access gate, HTTP error, and now network failure) to `ApiHttpError`
    // before it ever reaches a caller, so the wrapper's `else` branch is
    // unreachable dead code post-sprint. Removed rather than kept "just in
    // case" per P6/P3 (universal-principles.md).
    await api<{ ok: true }>('/api/auth/login', {
      method: 'POST',
      body: { password },
    });
    setAuthState('authed');
  }, []);

  const logout = useCallback(async () => {
    try {
      await api<{ ok: true }>('/api/auth/logout', { method: 'POST' });
    } catch {
      // Even if the request fails (network), clearing local state is fine —
      // the cookie is HttpOnly so we can't clear it ourselves; the server
      // call is the only way. We still reflect "logged out" in the UI.
    }
    setAuthState('unauthed');
  }, []);

  return { authState, login, logout, setAuthState, meNetworkError };
}
